<template>
  <div class="backtest-detail">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <el-spinner size="large"></el-spinner>
      <p>Loading test details...</p>
    </div>

    <!-- Detail Content -->
    <div v-else-if="testDetail" class="detail-content">
      <!-- Metadata Section -->
      <div class="metadata-section">
        <div class="metadata-header">
          <h4>Test ID: {{ testId }}</h4>
        </div>
        <JobParameters
          :parameters="rawParameters"
          :status="testDetail.status"
        />

        <!-- Results Section (if available) -->
        <!-- <div
          v-if="
            testDetail.results &&
              testDetail.status === 'Completed' &&
              !testDetail.isRedAlert
          "
          class="results-section"
        >
          <div class="results-header">
            <h5>Results</h5>
            <div class="results-grid">
              <div class="result-item">
                <label class="mb-0">Old Count:</label>
                <span>{{ testDetail.results.oldCount || 0 }}</span>
              </div>
              <div class="result-item">
                <label class="mb-0">New Count:</label>
                <span>{{ testDetail.results.newCount || 0 }}</span>
              </div>
            </div>
          </div>
        </div> -->

        <!-- Progress Bar (shown when processing) -->
        <div
          v-if="testDetail.status === 'Processing'"
          class="progress-container"
        >
          <label>Progress:</label>
          <el-progress
            :percentage="testDetail.progressPercentage"
            :text-inside="true"
            :stroke-width="25"
            status="success"
          ></el-progress>
        </div>

        <!-- Error Message (shown when test failed) -->
        <div v-if="testDetail.status === 'Failed'" class="error-message">
          <i class="el-icon-warning"></i>
          <span>Test execution failed. No results available.</span>
        </div>
        <!-- Overall Summary (Level 1) -->
        <SummaryPanel :summary="topSummary" />
      </div>

      <!-- Chart Section (only shown if test succeeded) -->
      <div
        v-if="
          testDetail.status !== 'Failed' &&
            (testDetail.chartsUrl || padCycles.length > 0)
        "
        class="chart-section"
      >
        <!-- Subject Selector (Device/Patient) + Summary -->
        <div v-if="subjects.length > 0" class="selector-summary-row">
          <el-select
            v-model="selectedSubject"
            @change="handleSubjectChange"
            size="small"
            placeholder="Select device/patient"
          >
            <el-option
              v-for="subject in subjects"
              :key="subject.id"
              :label="subject.label"
              :value="subject.id"
            >
            </el-option>
          </el-select>
          <SummaryPanel v-if="subjectSummary" :summary="subjectSummary" />
        </div>

        <!-- Cycle Selector + Cycle Summary -->
        <div v-if="padCycles.length > 0" class="selector-summary-row">
          <el-select
            v-model="selectedPadCycle"
            @change="handlePadCycleChange"
            size="small"
            placeholder="Select a cycle"
          >
            <el-option
              v-for="cycle in padCycles"
              :key="cycle.id"
              :label="cycle.label"
              :value="cycle.id"
            >
            </el-option>
          </el-select>
          <SummaryPanel v-if="cycleSummary" :summary="cycleSummary" />
        </div>

        <!-- Chart Controls and Filter are inside BackTestChart -->

        <BackTestChart
          :chartUrl="testDetail.chartsUrl"
          :jobId="testId"
          :jobType="testDetail.jobType"
          :s1Events="chartS1Events"
          :s2Events="chartS2Events"
          :oldTimeStamps="chartOldTimeStamps"
          :newTimeStamps="chartNewTimeStamps"
          :filters="activeFilters"
          :cycleData="selectedCycleData"
          :filterColumns="filterAvailableColumns"
          :filterData="chartRawData"
          :downloadDisabled="!testDetail.chartsUrl"
          @selection-changed="handleSelectionChanged"
          @chart-loaded="handleChartLoaded"
          @download="downloadFullResults"
          @filter-change="handleFilterChange"
          ref="chart"
        />
      </div>

      <!-- Data Table Section -->
      <div class="table-section">
        <BackTestDataTable
          :data="selectedData"
          :loading="loadingSelection"
          :filters="activeFilters"
        />
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="error-state">
      <p>Failed to load test details</p>
      <el-button @click="loadTestDetail" type="primary">Retry</el-button>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import BackTestApiService from '@/services/BackTestApiService'
import BackTestChart from './BackTestChart.vue'
import BackTestDataTable from './BackTestDataTable.vue'
import JobParameters from './JobParameters.vue'
import SummaryPanel from './SummaryPanel.vue'
import swal from 'sweetalert'
import { jobTypes } from '@/const'

export default {
  name: 'BackTestDetail',
  components: {
    BackTestChart,
    BackTestDataTable,
    JobParameters,
    SummaryPanel
  },
  props: {
    testId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      testDetail: null,
      loading: false,
      selectedData: [],
      loadingSelection: false,
      pollingInterval: null,
      // Subject (Device/Patient) selection
      subjects: [], // Array of { podMac/patientId, displayName, cycles }
      selectedSubject: null,
      // Pad cycle selection (for red alert)
      selectedPadCycle: null,
      padCycles: [],
      selectedCycleData: null,
      rawParameters: {},
      activeFilters: [],
      filterAvailableColumns: [],
      chartRawData: [],
      lastFetchSelection: null,
      // Summary data at 3 levels
      topSummary: null,
      subjectSummary: null,
      cycleSummary: null
    }
  },
  computed: {
    chartS1Events() {
      return (this.testDetail && this.testDetail.s1Events) || []
    },
    chartS2Events() {
      return (this.testDetail && this.testDetail.s2Events) || []
    },
    chartOldTimeStamps() {
      if (!this.testDetail) return []
      if (this.testDetail.isRedAlert) {
        return this.testDetail.oldTimeStamps || []
      }
      const r = this.testDetail.results
      return (r && r.oldTimeStamps) || []
    },
    chartNewTimeStamps() {
      if (!this.testDetail) return []
      if (this.testDetail.isRedAlert) {
        return this.testDetail.newTimeStamps || []
      }
      const r = this.testDetail.results
      return (r && r.newTimeStamps) || []
    }
  },
  created() {
    // Create debounced version of fetchSliceData with 1000ms delay to prevent multiple API calls
    this.debouncedFetchSliceData = debounce(this.fetchSliceData, 1000)
  },
  watch: {
    testId: {
      immediate: true,
      handler() {
        // Clear selected data and filters when switching tests
        this.selectedData = []
        this.activeFilters = []
        // Clear filter component inputs
        this.$nextTick(() => {
          if (this.$refs.chart) {
            this.$refs.chart.clearAllFilters()
          }
        })
        this.loadTestDetail()
      }
    }
  },
  beforeDestroy() {
    this.stopPolling()
    if (this.debouncedFetchSliceData) {
      this.debouncedFetchSliceData.cancel()
    }
  },
  methods: {
    async loadTestDetail() {
      this.loading = true
      try {
        const response = await BackTestApiService.getBackTestDetail(this.testId)
        const data = response.data

        // Parse Parameters from JSON string
        let parameters = {}
        if (typeof data.Parameters === 'string') {
          parameters = JSON.parse(data.Parameters)
        } else {
          parameters = data.Parameters || {}
        }

        // Store raw parameters for display
        this.rawParameters = parameters

        // NEW FORMAT: Details is now an array of subjects (devices/patients)
        // For redalert: { PodMac, Cycles[] }
        // For newpad: { PodMac, OldPadCount, NewPadCount, OldTimeStamps, NewTimeStamps, ChartUrl, RawUrl }
        const isRedAlert = data.JobType === jobTypes.backTestingRedAlert

        // Extract top-level Summary (Level 1: Overall)
        this.topSummary = data.Summary || null

        if (Array.isArray(data.Details) && data.Details.length > 0) {
          // Build subject list with cycles (for redalert) or pad counts (for newpad)
          this.subjects = data.Details.map(detail => ({
            id: detail.PodMac || detail.PatientId,
            podMac: detail.PodMac,
            patientId: detail.PatientId,
            displayName: detail.DisplayName,
            label:
              detail.DisplayName ||
              detail.PodMac ||
              detail.PatientName ||
              'Unknown',
            // For redalert: cycles array
            cycles: detail.Cycles || [],
            yellowCount: detail.YellowCount || 0,
            redCount: detail.RedCount || 0,
            // For newpad: direct data fields
            oldPadCount: detail.OldPadCount,
            newPadCount: detail.NewPadCount,
            oldTimeStamps: detail.OldTimeStamps || [],
            newTimeStamps: detail.NewTimeStamps || [],
            chartUrl: detail.ChartUrl,
            rawUrl: detail.RawUrl,
            // Level 2: Per subject summary
            summary: detail.Summary || null
          }))

          // Select first subject by default
          if (this.subjects.length > 0 && !this.selectedSubject) {
            this.selectedSubject = this.subjects[0].id
          }

          // Update testDetail and pad cycles based on selected subject
          this.updateDetailFromSubjectSelection(data, parameters, isRedAlert)
        } else {
          // Fallback: no Details (e.g., when job Failed or still Processing)
          // Build basic testDetail with metadata only
          this.subjects = []
          this.selectedSubject = null
          this.padCycles = []
          this.selectedPadCycle = null
          this.subjectSummary = null
          this.cycleSummary = null

          // Get first device ID from parameters
          const deviceIds = parameters.deviceIds || []
          const patientIds = parameters.patientIds || []
          const firstDeviceId =
            (deviceIds && deviceIds.length > 0 ? deviceIds[0] : null) ||
            (patientIds && patientIds.length > 0 ? patientIds[0] : null) ||
            ''

          // Create basic testDetail with status
          this.testDetail = {
            id: data.Id,
            status: data.Status,
            progressPercentage: data.Progress,
            jobType: data.JobType,
            isRedAlert: isRedAlert,
            jobId: data.Id,
            deviceMac: firstDeviceId,
            algorithmType: parameters.algorithmType || '',
            startDate: parameters.startTime || '',
            endDate: parameters.endTime || '',
            timezone: parameters.offset || 0,
            totalDataPoints: data.TotalDataPoints || undefined,
            chartsUrl: null,
            rawUrl: null
          }
        }

        // Start polling if status is Processing or Queued
        if (
          this.testDetail &&
          (this.testDetail.status === 'Processing' ||
            this.testDetail.status === 'Queued')
        ) {
          this.startPolling()
        } else {
          this.stopPolling()
        }

        this.loading = false
      } catch (error) {
        swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load test details'
        })
        this.testDetail = null
      } finally {
        this.loading = false
      }
    },

    updateDetailFromSubjectSelection(data, parameters, isRedAlert) {
      // Find selected subject
      const selectedSubjectObj = this.subjects.find(
        d => d.id === this.selectedSubject
      )
      if (!selectedSubjectObj) return

      // Set subject-level summary (Level 2)
      this.subjectSummary = selectedSubjectObj.summary || null

      // Build testDetail with basic info
      this.testDetail = {
        id: data.Id,
        status: data.Status,
        progressPercentage: data.Progress,
        jobType: data.JobType,
        isRedAlert: isRedAlert,
        jobId: data.Id,
        deviceMac:
          selectedSubjectObj.podMac || selectedSubjectObj.patientId || '',
        algorithmType: parameters.algorithmType || '',
        startDate: parameters.startTime || '',
        endDate: parameters.endTime || '',
        timezone: parameters.offset || 0,
        totalDataPoints: data.TotalDataPoints || undefined
      }

      // Check if subject has cycles with ChartUrl (volume-estimation or red-alert)
      const hasCyclesWithChartUrl =
        Array.isArray(selectedSubjectObj.cycles) &&
        selectedSubjectObj.cycles.length > 0 &&
        selectedSubjectObj.cycles[0].ChartUrl

      if (hasCyclesWithChartUrl) {
        // For back-testing-redalert and back-testing-volume-estimation: Details[subject].Cycles[cycle]
        // Populate pad cycles from selected subject
        this.padCycles = selectedSubjectObj.cycles.map(cycle => ({
          id: cycle.PadCycleId,
          label: `${new Date(
            cycle.LocalStartTime
          ).toLocaleString()} - ${new Date(
            cycle.LocalEndTime
          ).toLocaleString()}`,
          localStartTime: cycle.LocalStartTime,
          localEndTime: cycle.LocalEndTime,
          utcStartTime: cycle.UtcStartTime,
          utcEndTime: cycle.UtcEndTime,
          oldRedAlertLocal: cycle.OldRedAlertLocal,
          oldRedAlertUtc: cycle.OldRedAlertUtc,
          newRedAlertLocal: cycle.NewRedAlertLocal,
          newRedAlertUtc: cycle.NewRedAlertUtc,
          s1Events: cycle.S1Events || [],
          s2Events: cycle.S2Events || [],
          chartUrl: cycle.ChartUrl,
          rawUrl: cycle.RawUrl,
          PadWeight: cycle.PadWeight,
          FinalVolume: cycle.FinalVolume,
          summary: cycle.Summary || null
        }))

        // Select first pad cycle by default
        if (this.padCycles.length > 0 && !this.selectedPadCycle) {
          this.selectedPadCycle = this.padCycles[0].id
        }

        // Update chart URLs and events from selected pad cycle
        const selectedCycle =
          this.padCycles.find(c => c.id === this.selectedPadCycle) ||
          this.padCycles[0]
        if (selectedCycle) {
          // Store cycle data for properties display
          this.selectedCycleData = {
            PadWeight: selectedCycle.PadWeight,
            FinalVolume: selectedCycle.FinalVolume
          }

          this.testDetail.chartsUrl = selectedCycle.chartUrl
          this.testDetail.rawUrl = selectedCycle.rawUrl
          this.testDetail.s1Events = selectedCycle.s1Events || []
          this.testDetail.s2Events = selectedCycle.s2Events || []
          this.testDetail.oldTimeStamps =
            selectedCycle.oldRedAlertLocal !== '0001-01-01T00:00:00'
              ? [selectedCycle.oldRedAlertLocal]
              : []
          this.testDetail.newTimeStamps =
            selectedCycle.newRedAlertLocal !== '0001-01-01T00:00:00'
              ? [selectedCycle.newRedAlertLocal]
              : []

          // Set cycle-level summary (Level 3)
          this.cycleSummary = selectedCycle.summary || null

          // Set cycle-level summary (Level 3)
          this.cycleSummary = selectedCycle.summary || null
        }
      } else {
        // For back-testing-newpad: Details[subject] with OldPadCount, NewPadCount, etc
        this.padCycles = []
        this.selectedPadCycle = null
        this.cycleSummary = null

        // Set chart URLs and data directly from subject
        this.testDetail.chartsUrl = selectedSubjectObj.chartUrl
        this.testDetail.rawUrl = selectedSubjectObj.rawUrl

        // Set results for newpad
        this.testDetail.results = {
          oldCount: selectedSubjectObj.oldPadCount || 0,
          newCount: selectedSubjectObj.newPadCount || 0,
          oldTimeStamps: selectedSubjectObj.oldTimeStamps || [],
          newTimeStamps: selectedSubjectObj.newTimeStamps || []
        }
      }
    },

    handleSubjectChange(subjectId) {
      this.selectedSubject = subjectId
      // Reset pad cycle selection when subject changes
      this.selectedPadCycle = null

      const selectedSubjectObj = this.subjects.find(
        d => d.id === this.selectedSubject
      )
      if (!selectedSubjectObj) return

      // Update subject-level summary (Level 2)
      this.subjectSummary = selectedSubjectObj.summary || null
      this.cycleSummary = null

      // Check if subject has cycles with ChartUrl (volume-estimation or red-alert)
      const hasCyclesWithChartUrl =
        Array.isArray(selectedSubjectObj.cycles) &&
        selectedSubjectObj.cycles.length > 0 &&
        selectedSubjectObj.cycles[0].ChartUrl

      if (hasCyclesWithChartUrl) {
        // For redalert and volume-estimation: update pad cycles from subject
        this.padCycles = selectedSubjectObj.cycles.map(cycle => ({
          id: cycle.PadCycleId,
          label: `${new Date(
            cycle.LocalStartTime
          ).toLocaleString()} - ${new Date(
            cycle.LocalEndTime
          ).toLocaleString()}`,
          localStartTime: cycle.LocalStartTime,
          localEndTime: cycle.LocalEndTime,
          utcStartTime: cycle.UtcStartTime,
          utcEndTime: cycle.UtcEndTime,
          oldRedAlertLocal: cycle.OldRedAlertLocal,
          oldRedAlertUtc: cycle.OldRedAlertUtc,
          newRedAlertLocal: cycle.NewRedAlertLocal,
          newRedAlertUtc: cycle.NewRedAlertUtc,
          s1Events: cycle.S1Events || [],
          s2Events: cycle.S2Events || [],
          chartUrl: cycle.ChartUrl,
          rawUrl: cycle.RawUrl,
          PadWeight: cycle.PadWeight,
          FinalVolume: cycle.FinalVolume,
          summary: cycle.Summary || null
        }))

        // Select and update to first cycle
        if (this.padCycles.length > 0) {
          this.selectedPadCycle = this.padCycles[0].id
          this.handlePadCycleChange(this.selectedPadCycle)
        }
      } else {
        // For newpad: update chart URLs and results from subject
        this.padCycles = []
        this.testDetail.chartsUrl = selectedSubjectObj.chartUrl
        this.testDetail.rawUrl = selectedSubjectObj.rawUrl
        this.testDetail.results = {
          oldCount: selectedSubjectObj.oldPadCount || 0,
          newCount: selectedSubjectObj.newPadCount || 0,
          oldTimeStamps: selectedSubjectObj.oldTimeStamps || [],
          newTimeStamps: selectedSubjectObj.newTimeStamps || []
        }

        // Clear selected data and force chart reload
        this.selectedData = []
        this.$nextTick(() => {
          if (this.$refs.chart) {
            this.$refs.chart.loadChartData()
          }
        })
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollingInterval = setInterval(async () => {
        const response = await BackTestApiService.getBackTestStatus(this.testId)
        const data = response.data
        this.testDetail.status = data.Status
        this.testDetail.progressPercentage = data.Progress

        // Stop polling when complete or failed
        if (data.Status === 'Completed' || data.Status === 'Failed') {
          this.stopPolling()
          this.loadTestDetail() // Reload full details
          this.$emit('refresh')
        }
      }, 3000) // Poll every 3 seconds
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
    handlePadCycleChange(cycleId) {
      // Find the selected cycle
      const selectedCycle = this.padCycles.find(c => c.id === cycleId)
      if (!selectedCycle) return

      // Store cycle data for properties display
      this.selectedCycleData = {
        PadWeight: selectedCycle.PadWeight,
        FinalVolume: selectedCycle.FinalVolume
      }

      // Update testDetail with new cycle data
      this.testDetail.chartsUrl = selectedCycle.chartUrl
      this.testDetail.rawUrl = selectedCycle.rawUrl
      this.testDetail.s1Events = selectedCycle.s1Events || []
      this.testDetail.s2Events = selectedCycle.s2Events || []
      this.testDetail.oldTimeStamps =
        selectedCycle.oldRedAlertLocal !== '0001-01-01T00:00:00'
          ? [selectedCycle.oldRedAlertLocal]
          : []
      this.testDetail.newTimeStamps =
        selectedCycle.newRedAlertLocal !== '0001-01-01T00:00:00'
          ? [selectedCycle.newRedAlertLocal]
          : []

      // Update cycle-level summary (Level 3)
      this.cycleSummary = selectedCycle.summary || null

      // Clear selected data
      this.selectedData = []

      // Force chart to reload by triggering re-render
      this.$nextTick(() => {
        if (this.$refs.chart) {
          this.$refs.chart.loadChartData()
        }
      })
    },
    handleSelectionChanged(selection) {
      if (!selection || !selection.startTime || !selection.endTime) {
        this.selectedData = []
        return
      }
      // Prevent duplicate calls - check if this is the same selection
      const selectionKey = `${selection.startTime}|${selection.endTime}`
      if (this.lastFetchSelection === selectionKey) {
        return
      }
      this.lastFetchSelection = selectionKey
      this.debouncedFetchSliceData(selection)
    },
    async fetchSliceData(selection) {
      this.loadingSelection = true
      try {
        const response = await BackTestApiService.getRawSlice(
          this.testId,
          selection.startTime,
          selection.endTime,
          this.testDetail.timezone,
          this.testDetail.rawUrl
        )
        this.selectedData = response.data
        // Update available columns for filter
        if (this.selectedData && this.selectedData.length > 0) {
          this.filterAvailableColumns = Object.keys(this.selectedData[0])
        }
      } finally {
        this.loadingSelection = false
      }
    },
    handleFilterChange(filters) {
      this.activeFilters = filters
      // Manually trigger chart re-render when Apply Filter is clicked
      this.$nextTick(() => {
        if (this.$refs.chart) {
          this.$refs.chart.initChart()
        }
      })
    },
    handleChartLoaded(chartData) {
      // When chart loads, get available columns from chart data
      if (chartData && chartData.availableColumns) {
        this.filterAvailableColumns = chartData.availableColumns
      }
      if (chartData && chartData.rawData) {
        this.chartRawData = chartData.rawData
      }
    },
    downloadFullResults() {
      if (this.testDetail && this.testDetail.rawUrl) {
        window.open(this.testDetail.rawUrl, '_blank')
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleString()
    },
    formatMetricLabel(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
    }
  }
}
</script>

<style scoped lang="scss">
.backtest-detail {
  .loading-state,
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: #666;

    p {
      margin-top: 15px;
      font-size: 16px;
    }
  }

  .detail-content {
    .metadata-section,
    .chart-section,
    .table-section {
      margin-bottom: 15px;
      padding: 20px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background-color: #fff;

      h4,
      h5 {
        margin-bottom: 15px;
        font-weight: 600;
      }

      h4 {
        font-size: 18px;
      }

      h5 {
        font-size: 14px;
        color: #666;
      }
    }

    .chart-section {
      .selector-summary-row {
        display: flex;
        align-items: flex-start;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
        padding-bottom: 12px;
        border-bottom: 1px solid #eee;

        .el-select {
          min-width: 300px;
          flex-shrink: 0;
        }

        ::v-deep .summary-panel {
          margin-top: 0;
          padding-top: 0;
          border-top: none;
          flex: 1;
          min-width: 0;
        }
      }
    }

    .metadata-section {
      .progress-container {
        margin-top: 20px;

        label {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
          font-size: 13px;
        }

        .progress {
          height: 25px;
          font-size: 13px;
        }
      }

      .error-message {
        margin-top: 20px;
        padding: 15px;
        background-color: #f8d7da;
        border: 1px solid #f5c6cb;
        border-radius: 4px;
        color: #721c24;
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          font-size: 18px;
        }

        span {
          font-size: 14px;
        }
      }

      .results-section {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #eee;

        .results-header {
          display: flex;
          align-items: center;
          gap: 15px;

          h5 {
            font-size: 13px;
            font-weight: 600;
            margin-bottom: 0;
            color: #333;
            white-space: nowrap;
          }

          .results-grid {
            display: flex;
            gap: 15px;
            flex: 1;
          }
        }

        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 5px;

          .result-item {
            label {
              font-weight: 600;
              margin-right: 8px;
              font-size: 13px;
              color: #555;
            }

            span {
              font-size: 13px;
              color: #333;
            }

            &.full-width {
              grid-column: 1 / -1;

              span {
                display: block;
                margin-top: 5px;
                padding: 8px;
                background-color: #f8f9fa;
                border-radius: 4px;
                font-family: monospace;
                font-size: 12px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
